import { config } from '../config.js';

/**
 * Login pela NOSSA API (/v1/auth/*). O app não fala com o Cognito: quem fala é o backend.
 * Erros chegam com código estável e mensagem pronta em PT-BR:
 *   INVALID_CREDENTIALS, USER_NOT_CONFIRMED, USER_BLOCKED, EMAIL_IN_USE, WEAK_PASSWORD,
 *   INVALID_CODE, CODE_EXPIRED, TOO_MANY_ATTEMPTS, SESSION_EXPIRED, AUTH_UNAVAILABLE, NETWORK...
 */

export class AuthError extends Error {
  /** @param {string} code @param {string} message @param {number} status */
  constructor(code, message, status) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
  }
}

async function call(path, body) {
  let res;
  try {
    res = await fetch(`${config.apiUrl}/v1/auth${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthError('NETWORK', 'Sem conexão. Verifique sua internet.', 0);
  }
  if (res.status === 204) return null;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new AuthError(e.code ?? `HTTP_${res.status}`, e.message ?? 'Não foi possível concluir. Tente novamente.', res.status);
  }
  return data;
}

export const auth = {
  /** Cria a conta. O código de confirmação vai para o e-mail. */
  signUp: (email, password) => call('/signup', { email, password }),
  confirmSignUp: (email, code) => call('/confirm', { email, code }),
  resendCode: (email) => call('/resend', { email }),
  /** @returns {Promise<{ accessToken: string, refreshToken: string, expiresIn: number, email: string, username: string }>} */
  login: (email, password) => call('/login', { email, password }),
  /**
   * username: identificador interno do Cognito devolvido no login (exigido pela API para renovar).
   * @returns {Promise<{ accessToken: string, expiresIn: number }>}
   */
  refresh: (refreshToken, username) => call('/refresh', { refreshToken, username }),
  forgotPassword: (email) => call('/forgot', { email }),
  confirmForgotPassword: (email, code, password) => call('/reset', { email, code, password }),
  /** Encerra a sessão (revoga o refresh token no servidor). */
  logout: (refreshToken) => call('/logout', { refreshToken }),
};
